import React, { useCallback, useState, useEffect, useMemo, useRef } from "react";
import { Button, Spin } from "antd";
import Icon, { ExclamationCircleFilled, SyncOutlined } from "src/icons";
import { RouteComponentProps } from "react-router";
import ReactGA from "react-ga4";
import Tabs, { TabData } from "src/shared/components/Tabs";

import "./index.scss";
import TopNavbar, { MenuItem } from "src/shared/components/Navbar/NewNavbar";
import DetailsScroller from "src/scenes/India2MIS/Details/DetailsScroller";
import CustomLineChart from "src/shared/components/LineChart";
import FiltersPane, { SelectedFilterValues } from "src/shared/components/FiltersPanev2";
import PinMetric from "src/shared/components/PinMetricModal";
import { REFRESH_ICON_ANIMATION, RunningAnobisMode, ThisWeekTabNames } from "src/shared/config";
import { TabNames } from "src/api/common";
import { DetailMetricType } from "src/shared/models/DetailsModel";
// import { ReactComponent as CompareIcon } from "src/assets/chart-compare.svg";
import { useMetricsMetadata } from "src/shared/hooks";
import NSStatusHandler from "src/shared/components/NSStatusHandler";
import { truncate, safeGet, reduxOperationToStatus, mergeStatuses } from "src/shared/utils";
import {
  setFiltersFromL2Page,
  setFilters,
  fetchDetails,
  fetchNestedTreeData,
  clearDetails,
  addPin,
  setDimensions,
  filtersReset,
  FetchDetailsPayload,
  fetchTableCharts,
  FetchDistFiltersPayload,
} from "src/store/details";
import { changeTimeTabAppWide, filtersConfigReset, setComingBackFromDetailsPage, UserRoles } from "src/store/app";
import { useRedux } from "src/shared/hooks";
import { IRootState } from "src/shared/types";
import { IReduxOperations, IActionFactory, IObject } from "src/store";
import { animated, useSpring } from "react-spring";
import styled from "@emotion/styled";
import colors from "src/styles/variables.scss";
import { getUserRoles } from "src/api/app";
import MobileTable from "src/scenes/India2MIS/Details/MobileTable.jsx";
import { HomeFilled } from "@ant-design/icons";
import { devLogging } from "src/utils/logger";
import { useLocation } from "react-router-dom";
import { relative } from "path";
import HourlyChart from "./HourlyChart";
import loadingGif from "./loading.gif";
import { isL2 } from "src/utils/misRoleUtils";

const DEFAULT_COMPARE_NO = 2;

// Add a navigation key to force rerender on route changes
const useForceRender = () => {
  const [renderKey, setRenderKey] = useState(0);
  const location = useLocation();

  // Update render key when URL path changes
  useEffect(() => {
    setRenderKey((prev) => prev + 1);
  }, [location.pathname]);

  return renderKey;
};

/**
 * Refer to wiki on bitbucket to understand filter logic
 * Metric type(metric vs pinned metric) is decided from the url
 * Different filter logic for both types of metrics
 * Note that once the page is rendered, and filters are populated, the filter can be changed
 */

const RefreshBadge = styled(animated.button)`
  border: none;
  border-radius: 16px;
  background: transparent;
  font-size: 12px;
  width: auto;
  line-height: 22px;
  padding: 0px 11px 0px 3px;
  display: flex;
  align-items: center;
  justify-self: end;
  margin-left: 20px;
  position: relative;
  outline: none;
  flex-direction: row-reverse;
  cursor: pointer;
  left: 9%;
  min-width: 126px;
  justify-content: flex-end;

  > span {
    /* margin-right: 10px; */
  }
`;

const IconHolder = styled.div`
  font-size: 16px;
  line-height: 13px;
`;

const CALL_DELAY = 1500;

type OptionsType = {
  className: string;
  iconClass: string;
  handleClick(): void;
};
type OptionGroupType = Array<OptionsType>;

type Tabtypes = {
  tabName: string;
}[];

const TOP_CHARTS = [
  { label: "NOW", valueField: "bigNumber", percentage: "vCompare" },
  // {label: "CURRENT GOAL", valueField: "currentGoal"},
  { label: "BENCHMARK", valueField: "benchMark" },
];

interface IDetailsMappedProps {
  charts: IReduxOperations;
  tableCharts: IObject<any>;
  filters: IObject<any>;
  l2Filters: IObject<any>;
  distributedFilters: IReduxOperations;
  selectedDimensions: Array<string>;
  selectedTab: string;
  filtersConfig: IReduxOperations;
  userRoles: IReduxOperations<UserRoles>;
}

/** TODO: Replace all the any types */
/** TODO: Remove all the unused Vars */
type MappedActionProps = {
  setFiltersFromL2Page();
  setFilters: IActionFactory<symbol, IObject<any>>;
  fetchDetails(payload: FetchDetailsPayload);
  fetchTableCharts(payload: any);
  fetchNestedTreeData(payload: FetchDistFiltersPayload);
  addPin(payload: string);
  clearDetails: IActionFactory<symbol, any>;
  setDimensions: IActionFactory<symbol, Array<string>>;
  filtersReset: IActionFactory<symbol, any>;
  filtersConfigReset: IActionFactory<symbol, any>;
  changeTimeTabAppWide: IActionFactory<symbol, string>;
  setComingBackFromDetailsPage: IActionFactory<symbol, boolean>;
};
const DetailsContainer: React.FC<RouteComponentProps<{ id: string; type: DetailMetricType }>> = ({
  history,
  match,
}) => {
  // Force component to fully remount on route changes
  const navigationKey = useForceRender();

  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: window.location.pathname, title: "IMCom Details" });
  }, []);

  // Add isFilterActive state
  const [isFilterActive, setIsFilterActive] = useState(false);

  // FIXME: Catch if `metricOrPinId` not a number
  const { id: metricOrPinId, type: metricType } = match.params;
  const [showDialog, setShowDialog] = useState(false); // controls visibility of filter pane
  const [showModal, setShowModal] = useState(false); // controls visibility of add pin modal
  const [modalPause, setModalPause] = useState(false); // retain modal info if filter pane opened from the modal, reopen modal after filter is closed
  const [pauseData, setPauseData] = useState(""); // same as above, saves name field  const lineChartContainer = useRef<HTMLDivElement>(null);
  const lineChartContainer = useRef<HTMLDivElement>(null);
  const [isRefreshClicked, setIsRefreshClicked] = useState(0);
  const [metrics, metricsStatus] = useMetricsMetadata();
  // Set loading state immediately when component renders with new ID
  const [isLoading, setIsLoading] = useState(true);

  const {
    setFiltersFromL2Page: setFiltersFromL2,
    filters,
    l2Filters,
    tableCharts,
    charts,
    selectedTab,
    setFilters: boundSetFilters,
    fetchDetails: fetchMetricDetails,
    fetchTableCharts: fetchTableChartsDetails,
    distributedFilters,
    fetchNestedTreeData: fetchDistributedFilters,
    addPin: addNewPin,
    setDimensions: setDimensionsAction,
    selectedDimensions,
    filtersReset: filtersResetAction,
    changeTimeTabAppWide: changeTimeTab,
    setComingBackFromDetailsPage: toggleComingBackFromDetailsPage,
    // filtersConfigReset: filtersConfigResetAction,
    filtersConfig,
    userRoles,
  } = useRedux<IRootState, IDetailsMappedProps, MappedActionProps>(
    (state) => ({
      filters: state.metricDetails.filters,
      l2Filters: state.category.filters,
      charts: state.metricDetails.charts,
      tableCharts: state.metricDetails.tableCharts,
      distributedFilters: state.metricDetails.distributedFilters,
      selectedTab: state.app.selectedTab,
      comingBackFromDetailsPage: state.app.comingBackFromDetailsPage,
      selectedDimensions: state.metricDetails.selectedDimensions,
      filtersConfig: state.app.filtersConfig,
      userRoles: state.app.userRoles,
    }),
    {
      setFiltersFromL2Page,
      setFilters,
      fetchDetails,
      fetchTableCharts,
      fetchNestedTreeData,
      clearDetails,
      addPin,
      setDimensions,
      filtersReset,
      filtersConfigReset,
      changeTimeTabAppWide,
      setComingBackFromDetailsPage,
    }
  );

  // Update the L1Filters useMemo to set isFilterActive
  const L1Filters = useMemo(() => {
    const filters: SelectedFilterValues = {};
    const savedFilters = localStorage.getItem("india2mis_filters");

    if (savedFilters) {
      const parsedFilters = JSON.parse(savedFilters);
      
      // Handle filters based on user role
      if (isL2(userRoles?.payload?.misRole)) {
        // For L2 users, only set city, poc_email_id, and l1_manager_email_id
        if (parsedFilters.city && parsedFilters.city.length > 0) {
          filters.city = parsedFilters.city;
        }
        if (parsedFilters.pocEmailId && parsedFilters.pocEmailId.length > 0) {
          filters.poc_email_id = parsedFilters.pocEmailId;
        }
        if (parsedFilters.l1Manager) {
          filters.l1_manager_email_id = Array.isArray(parsedFilters.l1Manager) ? parsedFilters.l1Manager : [parsedFilters.l1Manager];
        }
      } else {
        // For other users, include restaurant_id
        if (parsedFilters.restaurantId && parsedFilters.restaurantId.length > 0) {
          filters.restaurant_id = parsedFilters.restaurantId;
        }
        if (parsedFilters.city && parsedFilters.city.length > 0) {
          filters.city = parsedFilters.city;
        }
        if (parsedFilters.pocEmailId && parsedFilters.pocEmailId.length > 0) {
          filters.poc_email_id = parsedFilters.pocEmailId;
        }
      }
      
      // Set isFilterActive based on whether any filters are present
      setIsFilterActive(
        (!isL2(userRoles?.payload?.misRole) && parsedFilters.restaurantId?.length > 0) || 
        (parsedFilters.city && parsedFilters.city.length > 0) || 
        (parsedFilters.pocEmailId && parsedFilters.pocEmailId.length > 0) ||
        (parsedFilters.l1Manager && (Array.isArray(parsedFilters.l1Manager) ? parsedFilters.l1Manager.length > 0 : !!parsedFilters.l1Manager))
      );
    } else {
      setIsFilterActive(false);
    }
    
    return filters;
  }, [userRoles?.payload?.misRole]);

  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Track charts.pending changes to update isLoading state
  useEffect(() => {
    // When charts are no longer pending and we have a payload, stop loading
    if (!charts.pending && charts.payload && metricOrPinId == charts.payload?.id) {
      setIsLoading(false);
    }
  }, [charts.pending, charts.payload, metricOrPinId]);

  // To avoid few side effects of state change on page load
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Reset details state when component mounts
  useEffect(() => {
    // Immediately set loading state true when metric ID changes
    setIsLoading(true);

    // Clear any previous details when mounting a new component
    clearDetails();

    // Then fetch fresh details
    if (metricType !== DetailMetricType.PIN) {
      setFiltersFromL2();
    }
    devLogging("here is the L1Filtersss ", L1Filters);
    fetchMetricDetails({
      metricId: metricOrPinId,
      filters: L1Filters,
      metricType,
      fresh: true, // Always fetch fresh data
    });
  }, [metricOrPinId, navigationKey]); // Re-run if the metric ID changes or navigation occurs

  useEffect(() => {
    // users are always pain in the a**
    // They might visit category page (L2) and then directly visit pin detail page by typing URL

    devLogging("here is the L1Filtersss ", L1Filters);
    if (metricType !== DetailMetricType.PIN) {
      setFiltersFromL2();
    }
    fetchMetricDetails({
      metricId: metricOrPinId,
      filters: L1Filters,
      metricType,
    });

    devLogging("fetch charts ", charts);

    // TODO: Added table charts
    if (safeGet(charts, "payload.tableChartId") && safeGet(charts, "payload.metricId")) {
      // Create filters array based on user role
      let tableFilters: Array<{ field: string; value: string[] }> = [];
      
      if (isL2(userRoles.payload?.misRole)) {
        // For L2 users, only include city, poc_email_id, and l1_manager_email_id
        tableFilters = [
          ...(L1Filters.city ? [{ field: "city", value: L1Filters.city }] : [{ field: "city", value: userRoles.payload?.cities }]),
          ...(L1Filters.poc_email_id ? [{ field: "poc_email_id", value: L1Filters.poc_email_id }] : []),
          ...(L1Filters.l1_manager_email_id ? [{ field: "l1_manager_email_id", value: L1Filters.l1_manager_email_id }] : [])
        ];
      } else {
        // For other users, include restaurant_id
        tableFilters = [
          {
            field: "restaurant_id",
            value:
              (L1Filters.restaurant_id?.length > 0 ? L1Filters.restaurant_id : userRoles.payload?.restaurantIds) || [],
          },
          ...(L1Filters.city ? [{ field: "city", value: L1Filters.city }] : []),
          ...(L1Filters.poc_email_id ? [{ field: "poc_email_id", value: L1Filters.poc_email_id }] : []),
        ];
      }
      
      fetchTableChartsDetails({
        tableChartId: safeGet(charts, "payload.tableChartId"),
        metricId: safeGet(charts, "payload.metricId"),
        filters: tableFilters,
        metricType,
      });
    }
    devLogging("exported state value of table chart ", tableCharts);
  }, [safeGet(charts, "payload.tableChartId"), safeGet(charts, "payload.metricId"), L1Filters]);

  /** Removing the selected dimensions on page navigation/component unmount(and not on refresh) */
  useEffect(() => {
    return () => {
      setDimensionsAction([]);
      filtersResetAction();
      filtersConfigReset();
      // Clear details when unmounting to prevent stale state from showing when returning
      clearDetails();
    };
  }, []);

  const onToggleDialog = useCallback(() => {
    setShowDialog(!showDialog);
    // setShowModal(false);
  }, [showDialog]);

  const badgeRef = useRef();
  const applyRef = (target) => {
    if (!badgeRef.current) {
      badgeRef.current = target && target.getBoundingClientRect();
    }
  };

  const onRefresh = useCallback(() => {
    setIsRefreshClicked(Date.now());

    setTimeout(() => {
      fetchMetricDetails({
        metricId: metricOrPinId,
        filters: filters,
        fresh: true,
        metricType,
      });

      if (safeGet(charts, "payload.tableChartId") && safeGet(charts, "payload.metricId")) {
        // Create filters array based on user role
        let tableFilters: Array<{ field: string; value: string[] }> = [];
        
        if (isL2(userRoles.payload?.misRole)) {
          // For L2 users, only include city, poc_email_id, and l1_manager_email_id
          tableFilters = [
            ...(L1Filters.city ? [{ field: "city", value: L1Filters.city }] : []),
            ...(L1Filters.poc_email_id ? [{ field: "poc_email_id", value: L1Filters.poc_email_id }] : []),
            ...(L1Filters.l1_manager_email_id ? [{ field: "l1_manager_email_id", value: L1Filters.l1_manager_email_id }] : [])
          ];
        } else {
          // For other users, include restaurant_id
          tableFilters = [
            {
              field: "restaurant_id",
              value:
                (L1Filters.restaurant_id?.length > 0 ? L1Filters.restaurant_id : userRoles.payload?.restaurantIds) || [],
            },
            ...(L1Filters.city ? [{ field: "city", value: L1Filters.city }] : []),
            ...(L1Filters.poc_email_id ? [{ field: "poc_email_id", value: L1Filters.poc_email_id }] : []),
          ];
        }
        
        fetchTableChartsDetails({
          tableChartId: safeGet(charts, "payload.tableChartId"),
          metricId: safeGet(charts, "payload.metricId"),
          filters: tableFilters,
          metricType,
        });
      }
    }, CALL_DELAY);
  }, [filters, L1Filters, userRoles?.payload?.misRole, userRoles?.payload?.restaurantIds]);

  useEffect(() => {
    if (isRefreshClicked && Date.now() - isRefreshClicked > CALL_DELAY && safeGet(charts, "pending") === false) {
      setIsRefreshClicked(0);
    }
  }, [safeGet(charts, "pending"), isRefreshClicked]);

  const pinMetric = useCallback(() => {
    setShowModal(true);
  }, []);

  const handleChangeTab = useCallback(
    (tabData: TabData) => {
      changeTimeTab(tabData.key);
      fetchMetricDetails({ metricId: metricOrPinId, filters, metricType });
      filtersResetAction();
    },
    [filters, metricType, metricOrPinId]
  );

  const isFilterApplied = useMemo(
    () => Object.entries(filters).some(([key, value]: [string, any]): boolean => value.length !== 0),
    [filters]
  );

  const options: OptionGroupType = [
    {
      className: "option1",
      iconClass: "sync",
      handleClick: onRefresh,
    },
  ];

  const onBack = useCallback(() => {
    toggleComingBackFromDetailsPage(true);
    history.goBack();
  }, [history]);

  const syncIcon = (
    <IconHolder>
      {safeGet(charts, "payload.isStaleMetric", false) && !isRefreshClicked ? (
        <ExclamationCircleFilled />
      ) : (
        <SyncOutlined />
      )}
    </IconHolder>
  );

  // ********** Animations Start ************

  const iconStyles = useSpring(REFRESH_ICON_ANIMATION);

  const [refreshBadgeStyles, refreshBadgeApi] = useSpring(() => {
    const isStale = safeGet(charts, "payload.isStaleMetric", false);
    return {
      duration: 300,
      translateX: 0,
      translateY: 0,
      opacity: isStale ? 1 : 0.6,
      backgroundColor: "black",
      color: "black",
      zIndex: 1000,
      position: relative,
    };
  });

  const slideDownInitialState = { paddingTop: 0, duration: 300 };
  const [slideDownStyles, slideDownApi] = useSpring(() => slideDownInitialState);

  useEffect(() => {
    if (isRefreshClicked) {
      refreshBadgeApi({
        translateX:
          window.innerWidth / 2 - safeGet(badgeRef, "current.width", 0) / 2 - safeGet(badgeRef, "current.left", 0),
        translateY: 20,
        opacity: 1,
        backgroundColor: colors.canaryHighlight,
        color: colors.l2Background,
      });
      slideDownApi({
        paddingTop: 20,
      });
    } else {
      const isStale = safeGet(charts, "payload.isStaleMetric", false);
      refreshBadgeApi({
        translateX: 0,
        translateY: 0,
        opacity: isStale ? 1 : 0.6,
        backgroundColor: "transparent",
        color: isStale ? colors.anomalyIndicator : colors.paleYellow,
      });
      slideDownApi(slideDownInitialState);
    }
  }, [isRefreshClicked]);

  // ********** Animations End ************

  // Don't show previous metric name while data is being fetched for new one (Happens rarely)
  const metricName = metricOrPinId == charts.payload?.id ? charts.payload?.label : "";
  devLogging("my scharts ", charts);

  // Check if we're loading data for a different metric than what's currently displayed
  // Make this more aggressive to catch any loading state
  const isLoadingNewMetric = charts.pending || metricOrPinId != charts.payload?.id || isLoading || !charts.payload; // Also consider empty payload as loading

  // Show loading spinner when loading or just arrived to the page
  if (isLoadingNewMetric || isInitialLoading) {
    return (
      <div>
        <div style={{ padding: "10px", backgroundColor: "white" }}>
          <TopNavbar
            isHome={false}
            handleClick={onBack}
            headerText=""
            rootStyle="padding-bottom: 0; color: black; padding: 5px;"
          >
            <p style={{ marginLeft: "10px", fontWeight: "bold", fontSize: "1.5rem" }}> Loading... </p>
          </TopNavbar>
        </div>
        <div
          className="details-container-imcom"
          style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}
        >
          <img src={loadingGif} alt="Loading..." style={{ width: "350px", height: "300", borderRadius: "10%" }} />
        </div>
      </div>
    );
  }

  // Add styled components for filter chips
  const FilterChipsContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 0px;
    padding: 0 16px;
  `;

  const FilterChip = styled.div`
    background-color: ${colors.primaryColor}15;
    color: ${colors.primaryColor};
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    display: flex;
    align-items: center;
    gap: 4px;
  `;

  // @ts-ignore
  return (
    <div>
      {/*<div></div>*/}
      {/* Top Navigation Bar */}
      <div style={{ padding: "10px", backgroundColor: "white" }}>
        <TopNavbar
          isHome={false}
          handleClick={onBack}
          headerText=""
          rootStyle="padding-bottom: 0; color: black; padding: 5px;"
        >
          <p style={{ margin: "0", fontWeight: "bold", fontSize: "1.5rem" }}> {metricName} </p>

          {options.map((option) => (
            <MenuItem
              key={option.iconClass}
              {...option}
              showMarker={option.iconClass === "sliders" && isFilterApplied}
            />
          ))}
        </TopNavbar>
        
        {/* Add Filter Chips */}
        {isFilterActive && (
          <FilterChipsContainer>
            {L1Filters.city && L1Filters.city.length > 0 && (
              <FilterChip>
                {L1Filters.city.length > 1 
                  ? `Cities (${L1Filters.city.length})` 
                  : `City: ${L1Filters.city[0]}`}
              </FilterChip>
            )}
            {L1Filters.poc_email_id && L1Filters.poc_email_id.length > 0 && (
              <FilterChip>
                {L1Filters.poc_email_id.length > 1 
                  ? `POCs (${L1Filters.poc_email_id.length})` 
                  : `POC: ${L1Filters.poc_email_id[0].split('@')[0]}`}
              </FilterChip>
            )}
            {L1Filters.l1_manager_email_id && L1Filters.l1_manager_email_id.length > 0 && (
              <FilterChip>
                {L1Filters.l1_manager_email_id.length > 1 
                  ? `L1 Managers (${L1Filters.l1_manager_email_id.length})` 
                  : `L1: ${L1Filters.l1_manager_email_id[0].split('@')[0]}`}
              </FilterChip>
            )}
            {L1Filters.restaurant_id?.length > 0 && (
              <FilterChip>Restaurants: {L1Filters.restaurant_id.length}</FilterChip>
            )}
          </FilterChipsContainer>
        )}
        
        <RefreshBadge style={{ color: "green", zIndex: 1000, position: "relative" }} onClick={onRefresh} ref={applyRef}>
          <span style={{ marginRight: "30px" }}>
            updated {safeGet(charts, "payload.lastUpdatedTimeStamp", " __:__")}
          </span>
          {safeGet(charts, "pending", false) ? <animated.div style={iconStyles}>{syncIcon}</animated.div> : syncIcon}
        </RefreshBadge>
      </div>
      <div className="details-container-imcom">
        {/* Main Body */}
        <animated.div style={slideDownStyles} className="details-body-imcom">
          {/*Tabs will be required later */}
          {/*<Tabs*/}
          {/*  tabs={RunningAnobisMode.SHOW_IM_COM_TIME_TAB.map((tabName) => ({*/}
          {/*    title: tabName,*/}
          {/*    key: tabName,*/}
          {/*  }))}*/}
          {/*  page={selectedTab}*/}
          {/*  onChange={handleChangeTab}*/}
          {/*>*/}
          {/*{RunningAnobisMode.SHOW_IM_COM_TIME_TAB.map((tabName) => (*/}
          <div key={selectedTab}>
            <div className="detail-content-imcom">
              {/* Details Scroller */}
              <DetailsScroller data={TOP_CHARTS} charts={charts.payload} loading={charts.pending} />

              {devLogging("nested-container charts", charts, distributedFilters, selectedDimensions)}
            </div>
            {/* Table Charts */}

            <div className="scrollable-content-imcom">
              {" "}
              {isInitialLoading ? (
                <img
                  src={loadingGif}
                  alt="Loading..."
                  style={{ display: "block", margin: "20px auto", width: "350px", height: "300", borderRadius: "10%" }}
                />
              ) : tableCharts.success ? (
                // @ts-ignore
                <>
                  {/* <h2>Trend Chart</h2> */}
                  {/* <CustomLineChart
                      chart={safeGet(charts, "payload.trendChartData")}
                      isPending={safeGet(charts, "pending")}
                      width={safeGet(lineChartContainer, "current.clientWidth", 0)}
                      height={safeGet(lineChartContainer, "current.clientHeight", 0) * 0.85}
                      isPercentage={safeGet(charts, "payload.isPercentage", false)}
                  /> */}

                  {console.log("here is the tableCharts ", tableCharts)}
                  <MobileTable data={tableCharts} userRolesPayload={userRoles?.payload} />

                  {/* <HourlyChart /> */}
                </>
              ) : (
                <img
                  src={loadingGif}
                  alt="Loading..."
                  style={{ display: "block", margin: "20px auto", width: "350px", height: "300", borderRadius: "10%" }}
                />
              )}
            </div>
          </div>
          {/*))*/}
          {/*}*/}
          {/*</Tabs>*/}
        </animated.div>
      </div>
    </div>
  );
};

export default React.memo(DetailsContainer);
